import cn from 'classnames';
import { forwardRef } from 'react';

import { getTrendIcon, TREND_MARKER, TREND_MARKER_VARIANTS } from './trend-marker.utils';

import type { ForwardedRef, ReactElement } from 'react';

import type { TrendMarkerProps } from './trend-marker.types';

import './trend-marker.scss';

/**
 * TrendMarker Component
 *
 * The `TrendMarker` component is used to display a trend indicator with optional icons and highlighted states.
 * It supports different variants to indicate positive, negative, and neutral trends.
 *
 * ### Props
 *
 * - `className` (string): Custom class name for the container of the component.
 * - `children` (ReactNode): The content of the component.
 * - `highlighted` (boolean): If true, the trend marker will be highlighted.
 * - `useIcon` (boolean | ReactNode): Icon to be rendered within the trend marker.
 *   - If `useIcon` is a boolean, the default icons for positive or negative variants will be used.
 *   - If `useIcon` is a ReactNode, the provided icon will be used. This allows the consumer to pass a custom icon based on the variant value.
 * - `variant` (string): Variant of the trend marker. Can be one of 'neutral', 'positive', 'negative', 'positive-highlighted', 'negative-highlighted'.
 *
 * ### Usage
 *
 * ```tsx
 * import { TrendMarker } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <TrendMarker variant="positive" highlighted useIcon> Positive Trend </TrendMarker>
 *   );
 * }
 * ```
 */
const TrendMarker = forwardRef(
  (props: TrendMarkerProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      highlighted = false,
      useIcon = false,
      variant = TREND_MARKER.NEUTRAL,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const { defaultIcon, sign, trendClass } = TREND_MARKER_VARIANTS[variant];

    const Icon = getTrendIcon(useIcon, defaultIcon);

    const classes = cn(
      'rbui-trend-marker',
      trendClass,
      {
        'rbui-trend-marker--highlighted': highlighted,
        'rbui-trend-marker--with-icon': Boolean(Icon),
      },
      className,
    );

    return (
      <div className={classes} ref={forwardedRef} {...rest}>
        <div className="rbui-trend-marker__content">
          {Icon}

          <div className="rbui-trend-marker__text">
            {sign && <span>{sign}</span>}
            {children}
          </div>
        </div>
      </div>
    );
  },
);

TrendMarker.displayName = 'TrendMarker';

export default TrendMarker;
